import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { loadAlbums } from "../../../albumsApi";
import { getUploadBridge } from "../../../uploadBridge";
import { mediaUrl } from "../../../mediaAuth";
import { AvatarImage } from "../../components/AvatarImage";
import { firstNameOf } from "../home/CoupleHeader";
import { CreateAlbumDialog } from "../albums/CreateAlbumDialog";
import { AddAlbumCard } from "./AddAlbumCard";
import type { Album } from "../../../types";

function AddAlbumsSkeleton() {
  return (
    <div className="add-album-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="skeleton-card add-album-card-skeleton" />
      ))}
    </div>
  );
}

function countOf(album: Album): number {
  return album.mediaCount ?? (album.imageCount || 0) + (album.videoCount || 0);
}

// Small, condensed pair of avatars for this page's header corner - NOT a
// reuse of CoupleHeader.tsx's own component (that one carries a greeting/
// status line and is Home-only), just the same underlying identity data
// (presenceUsers + the shared currentUser* appState fields) at a much
// smaller size, matching the reference's subtle top-right treatment.
function MiniCoupleAvatars() {
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const myEmail = useAppState((s) => s.currentUserEmail);
  const myFullName = useAppState((s) => s.currentUserFullName);
  const myAvatarFilename = useAppState((s) => s.currentUserAvatarFilename);
  const partner = presenceUsers[0] ?? null;

  return (
    <div className="add-header-avatars" aria-hidden="true">
      <span className="add-header-avatar" title={firstNameOf(myFullName, myEmail)}>
        {myAvatarFilename ? (
          <AvatarImage
            filename={myAvatarFilename}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
            fallback={firstNameOf(myFullName, myEmail).charAt(0).toUpperCase() || "?"}
          />
        ) : (
          firstNameOf(myFullName, myEmail).charAt(0).toUpperCase() || "?"
        )}
      </span>
      {partner && (
        <>
          <i className="bi bi-heart-fill add-header-avatars-heart" />
          <span className="add-header-avatar" title={firstNameOf(partner.fullName, partner.email)}>
            {partner.avatarFilename ? (
              <AvatarImage
                filename={partner.avatarFilename}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                fallback={firstNameOf(partner.fullName, partner.email).charAt(0).toUpperCase() || "?"}
              />
            ) : (
              firstNameOf(partner.fullName, partner.email).charAt(0).toUpperCase() || "?"
            )}
          </span>
        </>
      )}
    </div>
  );
}

// Dedicated "/add" destination page - replaces the old pickAlbum() modal
// step that used to precede the native file picker (see
// uploadBridge.ts/UploadWidget.tsx). Tapping a destination here hands off
// immediately to the exact same native-picker/XHR-upload machinery the old
// modal fed into: this only replaces HOW the destination gets chosen, not
// what happens after. No "already selected media" preview step - this
// app's upload flow picks the destination BEFORE the OS file picker ever
// opens, so there's no in-app selection to preview at this point.
//
// Portals into #addView, mounted unconditionally like every other
// always-present screen (AlbumsListScreen.tsx et al.) - visibility is
// route-driven (AddRouteScreen.tsx). Also calls loadAlbums() itself,
// mirroring AlbumsListScreen.tsx's own redundant-but-safe load-on-mount.
export function AddScreen() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const albums = useAppState((s) => s.albums);
  const [loaded, setLoaded] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Immediate visual feedback the moment a destination is tapped (pink
  // border + check badge, matching the reference) - the native OS picker
  // takes the browser away right after, so this never persists as a real
  // "chosen" state across a session, just the instant of the tap itself.
  // Reset on every fresh arrival at /add rather than on unmount, since this
  // screen (like every other route-portal screen in this app) stays
  // mounted permanently and only toggles visibility.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Guards a double-tap from queuing a second native-picker open while the
  // first tap's browser dialog is still opening/closing - not tied to the
  // upload itself, which has its own progress UI (UploadWidget.tsx).
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadAlbums().finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (pathname === "/add") setSelectedId(null);
  }, [pathname]);

  const allMediaAlbum = albums.find((a) => a.id === "all") || null;
  // Exactly one non-"all" isDefault album can ever exist (the lazily
  // created Favorites album, server.js's ensureFavoritesAlbum()) - absent
  // entirely for an account that's never favorited anything yet, same as
  // the old modal (its target list was built from this same appState.albums
  // array, so it never showed a Favorites option either until one existed).
  const favoritesAlbum = albums.find((a) => a.isDefault && a.id !== "all") || null;
  const userAlbums = albums.filter((a) => !a.isDefault);
  // appState.albums is already sorted most-recently-updated-first
  // (albumsApi.ts's loadAlbums()) - a real, not fabricated, "you probably
  // want this again" signal. "all" is excluded: every upload touches it,
  // so it would always win and defeat the point. Omitted entirely (not a
  // fake placeholder) when there's no other album to recommend.
  const recentDestination = albums.find((a) => a.id !== "all") || null;

  function selectDestination(albumId: string): void {
    if (busy) return;
    setBusy(true);
    setSelectedId(albumId);
    getUploadBridge()?.openFilePickerForAlbum(albumId);
    setTimeout(() => setBusy(false), 1200);
  }

  const container = getVanillaEl<HTMLDivElement>("addView");
  if (!container) return null;

  return createPortal(
    <>
      <div className="add-page-header">
        <button type="button" className="add-page-back" aria-label="Back" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <span className="add-page-header-title">Add to Album</span>
        <MiniCoupleAvatars />
      </div>

      <div className="add-hero">
        <h1 className="add-hero-title">Choose where to save</h1>
        <p className="add-hero-subtitle">Keep your memories in the right place.</p>
      </div>

      {loaded && recentDestination && (
        <>
          <div className="add-section-label">Recent Destination</div>
          <button
            type="button"
            className={`add-recent-card${selectedId === recentDestination.id ? " is-selected" : ""}`}
            disabled={busy}
            onClick={() => selectDestination(recentDestination.id)}
          >
            <span className="add-recent-card-cover">
              {recentDestination.cover ? (
                <img src={mediaUrl(`/assets/thumbs/${recentDestination.cover}`)} alt="" />
              ) : (
                <span className="add-recent-card-cover-empty" aria-hidden="true">
                  <i className={recentDestination.isDefault ? "bi bi-heart-fill" : "bi bi-images"} />
                </span>
              )}
            </span>
            <span className="add-recent-card-body">
              <span className="add-recent-card-name">{recentDestination.name}</span>
              <span className="add-recent-card-count">
                {countOf(recentDestination) ? `${countOf(recentDestination)} memories` : "Empty album"}
              </span>
            </span>
            <span className="add-recent-card-chevron">
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </span>
          </button>
        </>
      )}

      <div className="add-section-label">Your Albums</div>
      {!loaded ? (
        <AddAlbumsSkeleton />
      ) : userAlbums.length ? (
        <div className="add-album-grid">
          {userAlbums.map((album) => (
            <AddAlbumCard
              key={album.id}
              album={album}
              busy={busy}
              selected={selectedId === album.id}
              onSelect={() => selectDestination(album.id)}
            />
          ))}
        </div>
      ) : (
        <div className="add-empty-state">
          <i className="bi bi-heart" aria-hidden="true" />
          <div className="add-empty-title">No albums yet</div>
          <p className="add-empty-subtitle">Create a private collection for your memories.</p>
        </div>
      )}

      <button type="button" className="add-create-row" onClick={() => setCreateOpen(true)}>
        <span className="add-create-row-icon">
          <i className="bi bi-plus-lg" aria-hidden="true" />
        </span>
        <span className="add-create-row-body">
          <span className="add-create-row-label">Create new album</span>
          <span className="add-create-row-value">Start a new collection</span>
        </span>
        <span className="add-create-row-chevron">
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </span>
      </button>

      {loaded && (favoritesAlbum || allMediaAlbum) && (
        <>
          <div className="add-section-label">Other Destinations</div>
          <div className="profile-group">
            {favoritesAlbum && (
              <button type="button" className="profile-row" disabled={busy} onClick={() => selectDestination(favoritesAlbum.id)}>
                <span className="profile-row-icon">
                  <i className="bi bi-heart-fill" style={{ color: "var(--pink-500)" }} aria-hidden="true" />
                </span>
                <span className="profile-row-body">
                  <span className="profile-row-label">Favorites</span>
                  <span className="profile-row-value">Your favorite memories</span>
                </span>
                <span className="profile-row-chevron">
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </span>
              </button>
            )}
            {allMediaAlbum && (
              <button type="button" className="profile-row" disabled={busy} onClick={() => selectDestination(allMediaAlbum.id)}>
                <span className="profile-row-icon">
                  <i className="bi bi-grid-fill" aria-hidden="true" />
                </span>
                <span className="profile-row-body">
                  <span className="profile-row-label">All Media</span>
                  <span className="profile-row-value">See everything in one place</span>
                </span>
                <span className="profile-row-chevron">
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </span>
              </button>
            )}
          </div>
        </>
      )}

      <CreateAlbumDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>,
    container,
  );
}
